import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import './CreateOrder.css';
import '../../../dashboard/Dashboard.css';
import SearchableSelect from '../../../../components/shared/SearchableSelect';
import { Input } from '../../../../components/ui/input';
import { Textarea } from '../../../../components/ui/textarea';
import { Button } from '../../../../components/ui/button';
import { cn } from '../../../../lib/utils';
import { useAuthStore } from '../../../../store/authStore';
import { apiService } from '../../../../services/apiService';
import { API_PATHS } from '../../../../utils/apiPaths';
import { resolveCountryDialCode } from '../../../../utils/countryPhoneCodes';
import {
  buildProcessPaymentHeaders,
  resolvePaymentCredentials,
} from '../../../../utils/paymentAuthHeaders';
import { extractApiData, isApiSuccess } from '../../../../utils/helpers';
import { validateEmail } from '../../../../utils/validation';
import CountryFlag from '../../../../components/shared/CountryFlag';
import {
  BsCreditCard,
  BsPersonFill,
  BsGeoAltFill,
  BsHouseFill,
  BsTranslate,
  BsGrid3X3Gap,
  BsShop,
  BsBuilding,
  BsFlagFill,
  BsGlobe2,
  BsEnvelopeFill,
  BsTelephoneFill,
  BsCurrencyDollar,
  BsCurrencyEuro,
  BsCurrencyPound,
  BsCurrencyRupee,
  BsCurrencyYen,
  BsCurrencyBitcoin,
  BsCurrencyExchange,
  BsArrowLeft,
  BsPlusCircle,
  BsCheckCircleFill,
  BsCheck2,
  BsChevronDown,
  BsLink45Deg,
} from 'react-icons/bs';
import { formatCardNumber, formatExpiryDate } from '../../../../utils/cardFormatting';

const DRAFT_KEY = 'create-order-draft';
const SERVICE_MAX = 1000;

const CURRENCY_ICONS: Record<string, React.ReactNode> = {
  USD: <BsCurrencyDollar />,
  EUR: <BsCurrencyEuro />,
  GBP: <BsCurrencyPound />,
  INR: <BsCurrencyRupee />,
  JPY: <BsCurrencyYen />,
  BTC: <BsCurrencyBitcoin />,
};

const getCurrencyIcon = (currency: string) =>
  CURRENCY_ICONS[currency?.toUpperCase()] ?? <BsCurrencyExchange />;

const formatCvv = (value: string) => value.replace(/\D/g, '').slice(0, 4);

const isS2SPaymentMethod = (label?: string) => label?.trim().toUpperCase() === 'S2S';

const normalizePostalCodeInput = (value: string) =>
  value.replace(/[^a-zA-Z0-9\s-]/g, '').toUpperCase();

const formatPostalCodeOnBlur = (value: string) => {
  const trimmed = value.trim().toUpperCase();
  const compact = trimmed.replace(/\s+/g, '');

  if (/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(compact)) {
    return `${compact.slice(0, 3)} ${compact.slice(3)}`;
  }

  return trimmed;
};

const ZipInput = React.memo(function ZipInput({
  value,
  onChange,
  hasError,
  draftRef,
}: {
  value: string;
  onChange: (value: string) => void;
  hasError: boolean;
  draftRef: React.MutableRefObject<string>;
}) {
  const [localValue, setLocalValue] = useState(value);
  const syncTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setLocalValue(value);
    draftRef.current = value;
  }, [value, draftRef]);

  useEffect(
    () => () => {
      if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    },
    [],
  );

  const syncToParent = useCallback(
    (next: string) => {
      draftRef.current = next;
      if (next !== value) onChange(next);
    },
    [onChange, value, draftRef],
  );

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const next = normalizePostalCodeInput(event.target.value);
    setLocalValue(next);
    draftRef.current = next;

    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    syncTimerRef.current = setTimeout(() => {
      syncToParent(next);
    }, 300);
  };

  const handleBlur = () => {
    if (syncTimerRef.current) clearTimeout(syncTimerRef.current);
    const formatted = formatPostalCodeOnBlur(draftRef.current);
    setLocalValue(formatted);
    syncToParent(formatted);
  };

  return (
    <Input
      type="text"
      autoComplete="postal-code"
      className={cn('pl-9', hasError && 'border-destructive')}
      value={localValue}
      onChange={handleChange}
      onBlur={handleBlur}
      placeholder="Enter postal code"
    />
  );
});

// Acquirers commonly require a real USPS 2-letter code for US billing addresses (seen from
// Paytently's own rejection: "state must be a valid USPS code for USA"). This is a property of
// what a valid US address looks like, not an acquirer-specific quirk, so it's safe to validate
// client-side regardless of which acquirer ends up processing the order.
const US_STATE_CODES = new Set([
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA', 'HI', 'ID', 'IL', 'IN', 'IA', 'KS',
  'KY', 'LA', 'ME', 'MD', 'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ', 'NM', 'NY',
  'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC', 'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV',
  'WI', 'WY', 'DC', 'AS', 'GU', 'MP', 'PR', 'VI',
]);
const isUsaCountryFlag = (flag?: string) => flag?.trim().toUpperCase() === 'US';

const LANGUAGES = [
  'English',
  'German',
  'French',
  'Spanish',
  'Italian',
  'Dutch',
  'Portuguese',
  'Polish',
  'Danish',
  'Swedish',
];

type PhoneCountryOption = {
  countryFlag: string;
  countryName: string;
  dialCode: string;
};

const PhoneCodeDropdown: React.FC<{
  value: string;
  countries: PhoneCountryOption[];
  onChange: (code: string) => void;
}> = ({ value, countries, onChange }) => {
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const ref = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDocClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
        setSearch('');
      }
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, [open]);

  useEffect(() => {
    if (open) searchRef.current?.focus();
    else setSearch('');
  }, [open]);

  const selected = countries.find((c) => c.countryFlag === value);
  const filtered = countries.filter((c) => {
    const q = search.trim().toLowerCase();
    if (!q) return true;
    return (
      c.countryName.toLowerCase().includes(q) ||
      c.countryFlag.toLowerCase().includes(q) ||
      c.dialCode.includes(q)
    );
  });

  return (
    <div className="co-phone-picker" ref={ref}>
      <button
        type="button"
        className={`co-phone-picker-trigger ${open ? 'open' : ''}`}
        aria-haspopup="listbox"
        aria-expanded={open}
        disabled={countries.length === 0}
        onClick={() => setOpen((o) => !o)}
        style={{height:"100%"}}
      >
        {selected ? (
          <CountryFlag countryFlag={selected.countryFlag} countryName={selected.countryName} />
        ) : (
          <span className="co-phone-picker-placeholder">Select</span>
        )}
        <BsChevronDown className={`co-phone-picker-chevron ${open ? 'open' : ''}`} />
      </button>
      {open && (
        <div className="co-phone-menu">
          <div className="co-phone-menu-search-wrap">
            <Input
              ref={searchRef}
              type="text"
              className="co-phone-menu-search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search country or code…"
              aria-label="Search country code"
            />
          </div>
          {filtered.length === 0 ? (
            <p className="co-phone-menu-empty">No countries match your search.</p>
          ) : (
            <ul className="co-phone-menu-list" role="listbox">
              {filtered.map((c) => (
                <li
                  key={c.countryFlag}
                  role="option"
                  aria-selected={value === c.countryFlag}
                  className={`co-phone-menu-item ${value === c.countryFlag ? 'selected' : ''}`}
                  onClick={() => {
                    onChange(c.countryFlag);
                    setOpen(false);
                  }}
                >
                  <CountryFlag countryFlag={c.countryFlag} countryName={c.countryName} />
                  <span className="co-phone-menu-name">{c.countryName}</span>
                  <span className="co-phone-menu-dial">{c.dialCode || '—'}</span>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
};

const BLANK = {
  paymentMethodId: '',
  merchantId: '',
  clientName: '',
  address: '',
  zip: '',
  language: '',
  merchantTerminalId: '',
  callbackUrl: '',
  redirectUrl: '',
  city: '',
  state: '',
  countryId: '',
  email: '',
  phoneCode: '',
  phone: '',
  amount: '',
  currency: '',
  cardNumber: '',
  cardholderName: '',
  expiryDate: '',
  cvv: '',
  service: '',
  isCheckout: false,
};

type CountryOption = {
  autoId: number;
  countryName: string;
  countryFlag: string;
  dialCode: string;
  currency: string;
};

type SelectOption = {
  autoId: number;
  label: string;
};

/** Until GET_ACTIVE_PAYMENT_METHODS exists, show these in the payment method dropdown. */
const DEFAULT_PAYMENT_METHODS: SelectOption[] = [
  { autoId: 1, label: 'S2S' },
  { autoId: 2, label: 'HPP' },
];

type TerminalMappingOption = {
  id: number;
  merchantId: number;
  merchantTerminalId: number;
  merchantTerminalName: string;
  acquirerTerminalName: string;
  status: string;
  redirectUrl: string;
  callbackUrl: string;
  apiKey?: string;
  merchantSecret?: string;
  currencies: number[];
};

const resolveCurrencyCodesFromCountryIds = (
  countryIds: number[],
  currencyById: Record<number, string>,
): string[] => {
  const codes = new Set<string>();
  countryIds.forEach((id) => {
    const currency = currencyById[id];
    if (currency) codes.add(currency);
  });
  return [...codes].sort((a, b) => a.localeCompare(b));
};

type MerchantOption = {
  id: number;
  name: string;
};

const normalizeMerchantOptions = (raw: unknown): MerchantOption[] => {
  const payload = extractApiData<unknown>(raw);
  const list = Array.isArray(payload)
    ? payload
    : Array.isArray((payload as { content?: unknown[] })?.content)
      ? (payload as { content: unknown[] }).content
      : [];

  return list
    .map((item) => {
      const row = item as Record<string, unknown>;
      return {
        id: Number(row.id ?? row.autoId ?? row.merchantId ?? 0),
        name: String(row.name ?? row.merchantName ?? row.companyName ?? '').trim(),
      };
    })
    .filter((merchant) => merchant.id > 0 && merchant.name.length > 0);
};

const isAdminMerchantSelectorRole = (role?: string) => {
  const normalized = String(role ?? '').trim().toUpperCase();
  return normalized === 'ADMIN' || normalized === 'SUPER_ADMIN';
};

const CreateOrder: React.FC = () => {
  const navigate = useNavigate();
  const authUser = useAuthStore((state) => state.user);
  const canSelectMerchant = isAdminMerchantSelectorRole(authUser?.adminType ?? authUser?.role);
  const [form, setForm] = useState({ ...BLANK });
  const zipDraftRef = useRef('');
  zipDraftRef.current = form.zip;
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);
  const [lastSaved, setLastSaved] = useState<string | null>(null);
  const [paymentMethods, setPaymentMethods] = useState<SelectOption[]>(DEFAULT_PAYMENT_METHODS);
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [currencyById, setCurrencyById] = useState<Record<number, string>>({});
  const [merchants, setMerchants] = useState<MerchantOption[]>([]);
  const [terminalMappings, setTerminalMappings] = useState<TerminalMappingOption[]>([]);
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const skipPersistRef = useRef(false);
  const [successSummary, setSuccessSummary] = useState<{
    clientName: string;
    amount: string;
    currency: string;
    email: string;
    method: string;
    isCheckout: boolean;
    checkoutUrl?: string;
    threeDsRequired?: boolean;
    paymentUrl?: string;
  } | null>(null);

  const resetForm = useCallback(() => {
    if (saveTimer.current) clearTimeout(saveTimer.current);
    localStorage.removeItem(DRAFT_KEY);
    skipPersistRef.current = true;
    setForm({
      ...BLANK,
      paymentMethodId: paymentMethods[0] ? String(paymentMethods[0].autoId) : '',
      phoneCode: countries[0]?.countryFlag ?? '',
      currency: '',
    });
    setErrors({});
    setApiError(null);
    setLastSaved(null);
  }, [paymentMethods, countries]);

  const phoneCountries = useMemo(
    () => [...countries].sort((a, b) => a.countryName.localeCompare(b.countryName)),
    [countries]
  );

  const selectedTerminalMapping = useMemo(
    () => terminalMappings.find((m) => String(m.id) === form.merchantTerminalId),
    [terminalMappings, form.merchantTerminalId],
  );

  const currencyOptions = useMemo(() => {
    if (!selectedTerminalMapping?.currencies.length) return [];
    return resolveCurrencyCodesFromCountryIds(selectedTerminalMapping.currencies, currencyById);
  }, [selectedTerminalMapping, currencyById]);

  const paymentMethodSelectOptions = useMemo(
    () => paymentMethods.map((m) => ({ value: String(m.autoId), label: m.label })),
    [paymentMethods]
  );

  const languageSelectOptions = useMemo(
    () => LANGUAGES.map((lang) => ({ value: lang, label: lang })),
    []
  );

  const terminalSelectOptions = useMemo(() => {
    const merchantFilterId = canSelectMerchant
      ? form.merchantId
      : (authUser?.merchantId ? String(authUser.merchantId) : form.merchantId);

    const filtered = merchantFilterId
      ? terminalMappings.filter((m) => String(m.merchantId) === merchantFilterId)
      : terminalMappings;

    return filtered.map((m) => ({
      value: String(m.id),
      label: `${m.merchantTerminalName} → ${m.acquirerTerminalName}`,
      searchText: `${m.merchantTerminalName} ${m.acquirerTerminalName}`,
    }));
  }, [terminalMappings, form.merchantId, canSelectMerchant, authUser?.merchantId]);

  const merchantSelectOptions = useMemo(
    () =>
      [...merchants]
        .sort((a, b) => a.name.localeCompare(b.name))
        .map((m) => ({
          value: String(m.id),
          label: m.name,
        })),
    [merchants]
  );

  const countrySelectOptions = useMemo(
    () =>
      [...countries]
        .sort((a, b) => a.countryName.localeCompare(b.countryName))
        .map((c) => ({
          value: String(c.autoId),
          label: c.countryName,
          searchText: c.countryFlag,
        })),
    [countries]
  );

  const currencySelectOptions = useMemo(
    () => currencyOptions.map((code) => ({ value: code, label: code })),
    [currencyOptions]
  );

  const selectedPaymentMethod = useMemo(
    () => paymentMethods.find((m) => String(m.autoId) === form.paymentMethodId),
    [paymentMethods, form.paymentMethodId]
  );

  const isS2S = isS2SPaymentMethod(selectedPaymentMethod?.label);

  useEffect(() => {
    if (isS2S && !form.isCheckout) return;
    setForm((prev) => {
      if (!prev.cardNumber && !prev.cardholderName && !prev.expiryDate && !prev.cvv) return prev;
      return { ...prev, cardNumber: '', cardholderName: '', expiryDate: '', cvv: '' };
    });
    setErrors((prev) => {
      if (!prev.cardNumber && !prev.cardholderName && !prev.expiryDate && !prev.cvv) return prev;
      const next = { ...prev };
      delete next.cardNumber;
      delete next.cardholderName;
      delete next.expiryDate;
      delete next.cvv;
      return next;
    });
  }, [isS2S, form.isCheckout]);

  const set = useCallback((field: keyof typeof BLANK, value: string) => {
    setForm((prev) => {
      if (prev[field] === value) return prev;
      return { ...prev, [field]: value };
    });
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const next = { ...prev };
      delete next[field];
      return next;
    });
  }, []);

  const handleZipChange = useCallback((value: string) => {
    set('zip', value);
  }, [set]);

  const handleMerchantChange = (merchantId: string) => {
    setForm((prev) => {
      const mapping = terminalMappings.find((m) => String(m.id) === prev.merchantTerminalId);
      const terminalMatches = mapping && String(mapping.merchantId) === merchantId;

      return {
        ...prev,
        merchantId,
        merchantTerminalId: terminalMatches ? prev.merchantTerminalId : '',
        callbackUrl: terminalMatches ? prev.callbackUrl : '',
        redirectUrl: terminalMatches ? prev.redirectUrl : '',
        currency: terminalMatches ? prev.currency : '',
      };
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next.merchantId;
      delete next.merchantTerminalId;
      delete next.callbackUrl;
      delete next.redirectUrl;
      return next;
    });
  };

  const handleTerminalChange = (terminalId: string) => {
    const mapping = terminalMappings.find((m) => String(m.id) === terminalId);
    const allowedCurrencies = mapping?.currencies.length
      ? resolveCurrencyCodesFromCountryIds(mapping.currencies, currencyById)
      : [];
    setForm((prev) => ({
      ...prev,
      merchantTerminalId: terminalId,
      merchantId: mapping ? String(mapping.merchantId) : prev.merchantId,
      callbackUrl: mapping?.callbackUrl ?? '',
      redirectUrl: mapping?.redirectUrl ?? '',
      currency: allowedCurrencies.includes(prev.currency)
        ? prev.currency
        : allowedCurrencies[0] ?? '',
    }));
    setErrors((prev) => {
      const next = { ...prev };
      delete next.merchantTerminalId;
      delete next.callbackUrl;
      delete next.redirectUrl;
      return next;
    });
  };

  const persistDraft = useCallback((values: typeof BLANK) => {
    try {
      localStorage.setItem(DRAFT_KEY, JSON.stringify(values));
      setLastSaved(new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }));
    } catch {
      /* ignore storage errors */
    }
  }, []);

  useEffect(() => {
    if (canSelectMerchant || !authUser?.merchantId) return;
    const merchantId = String(authUser.merchantId);
    setForm((prev) => (prev.merchantId === merchantId ? prev : { ...prev, merchantId }));
  }, [canSelectMerchant, authUser?.merchantId]);

  useEffect(() => {
    try {
      const raw = localStorage.getItem(DRAFT_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setForm((prev) => ({ ...prev, ...parsed }));
        setLastSaved('restored');
      }
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    if (skipPersistRef.current) {
      skipPersistRef.current = false;
      return;
    }
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(() => persistDraft(form), 600);
    return () => {
      if (saveTimer.current) clearTimeout(saveTimer.current);
    };
  }, [form, persistDraft]);

  useEffect(() => {
    if (!selectedTerminalMapping?.currencies.length) return;
    if (Object.keys(currencyById).length === 0) return;

    const allowed = resolveCurrencyCodesFromCountryIds(
      selectedTerminalMapping.currencies,
      currencyById,
    );
    if (!allowed.length) return;

    setForm((prev) => {
      if (allowed.includes(prev.currency)) return prev;
      return { ...prev, currency: allowed[0] };
    });
  }, [selectedTerminalMapping, currencyById]);

  useEffect(() => {
    const fetchLookups = async () => {
      let methods = DEFAULT_PAYMENT_METHODS;
      try {
        const methodsRes: any = await apiService.get(API_PATHS.GET_ACTIVE_PAYMENT_METHODS);
        const methodsRaw = Array.isArray(methodsRes?.data) ? methodsRes.data : Array.isArray(methodsRes) ? methodsRes : [];
        const apiMethods = methodsRaw
          .map((m: any) => ({
            autoId: Number(m.autoId ?? m.id ?? 0),
            label: String(m.paymentMethod ?? m.name ?? m.type ?? '').trim(),
          }))
          .filter((m: SelectOption) => m.autoId > 0 && m.label.length > 0);
        if (apiMethods.length > 0) methods = apiMethods;
      } catch {
        /* API not ready: keep S2S / HPP defaults */
      }

      setPaymentMethods(methods);
      setForm((prev) => {
        const hasSelection = prev.paymentMethodId && methods.some((m) => String(m.autoId) === prev.paymentMethodId);
        return hasSelection ? prev : { ...prev, paymentMethodId: String(methods[0].autoId) };
      });

      try {
        const lookupRequests: Promise<any>[] = [
          apiService.get(API_PATHS.GET_ACTIVE_COUNTRIES),
          apiService.post(API_PATHS.GET_MERCHANT_ACQUIRER_MAPPINGS, { page: 0, size: 500 }),
        ];
        if (canSelectMerchant) {
          lookupRequests.push(
            apiService.post(API_PATHS.GET_MERCHANTS_BY_STATUS, { status: 'ACTIVE' })
          );
        }

        const lookupResults = await Promise.all(lookupRequests);
        const countriesRes = lookupResults[0];
        const mappingsRes = lookupResults[1];
        const merchantsRes = canSelectMerchant ? lookupResults[2] : null;

        const countriesRaw = Array.isArray(countriesRes?.data) ? countriesRes.data : Array.isArray(countriesRes) ? countriesRes : [];
        const countryList = countriesRaw
          .map((c: any) => {
            const countryFlag = String(c.countryFlag ?? '').trim().toUpperCase();
            return {
              autoId: Number(c.autoId ?? c.id ?? 0),
              countryName: String(c.countryName ?? c.name ?? '').trim(),
              countryFlag,
              dialCode: resolveCountryDialCode(
                countryFlag,
                c.phoneCode ?? c.dialCode ?? c.countryPhoneCode ?? c.callingCode
              ),
              currency: String(c.currency ?? '').trim().toUpperCase(),
            };
          })
          .filter((c: CountryOption) => c.autoId > 0 && c.countryName.length > 0 && c.countryFlag.length === 2);
        setCountries(countryList);

        const currencyMap: Record<number, string> = {};
        countriesRaw.forEach((c: any) => {
          const id = Number(c.autoId ?? c.id ?? 0);
          const currency = String(c.currency ?? '').trim().toUpperCase();
          if (id > 0 && currency) currencyMap[id] = currency;
        });
        setCurrencyById(currencyMap);

        setForm((prev) => {
          const phoneValid = countryList.some((c: CountryOption) => c.countryFlag === prev.phoneCode);
          return {
            ...prev,
            phoneCode: phoneValid ? prev.phoneCode : countryList[0]?.countryFlag ?? '',
          };
        });

        if (merchantsRes && isApiSuccess(merchantsRes)) {
          setMerchants(normalizeMerchantOptions(merchantsRes));
        } else if (canSelectMerchant) {
          setMerchants([]);
        }

        const mappingsRaw = mappingsRes?.success
          ? (Array.isArray(mappingsRes?.data?.content) ? mappingsRes.data.content : [])
          : [];
        const mappingList = mappingsRaw
          .map((m: any): TerminalMappingOption => ({
            id: Number(m.id ?? 0),
            merchantId: Number(m.merchantId ?? 0),
            merchantTerminalId: Number(m.merchantTerminalId ?? 0),
            merchantTerminalName: String(m.merchantTerminalName ?? '—').trim(),
            acquirerTerminalName: String(m.acquirerTerminalName ?? '—').trim(),
            status: String(m.status ?? '').trim(),
            redirectUrl: String(m.redirectUrl ?? '').trim(),
            callbackUrl: String(m.webhookUrl ?? '').trim(),
            apiKey: String(m.apiKey ?? m.merchantKey ?? '').trim() || undefined,
            merchantSecret: String(m.merchantSecret ?? m.secretKey ?? m.secret ?? '').trim() || undefined,
            currencies: Array.isArray(m.currencies)
              ? m.currencies.map((id: unknown) => Number(id)).filter((id: number) => id > 0)
              : [],
          }))
          .filter((m: TerminalMappingOption) => m.id > 0 && m.status === 'ACTIVE');
        setTerminalMappings(mappingList);
      } catch (err) {
        console.error('Failed to load order form lookups', err);
      }
    };
    fetchLookups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!form.paymentMethodId) e.paymentMethodId = 'Payment method is required';
    if (!form.clientName.trim()) e.clientName = 'Client name is required';
    if (!form.address.trim()) e.address = 'Address is required';
    const zip = zipDraftRef.current.trim();
    if (!zip) e.zip = 'Zip is required';
    if (!form.language) e.language = 'Language is required';
    if (canSelectMerchant && !form.merchantId) e.merchantId = 'Merchant is required';
    if (!form.merchantTerminalId) e.merchantTerminalId = 'Terminal is required';
    if (!form.city.trim()) e.city = 'City is required';
    if (!form.state.trim()) {
      e.state = 'State / province is required';
    } else {
      const selectedCountry = countries.find((c) => String(c.autoId) === form.countryId);
      if (isUsaCountryFlag(selectedCountry?.countryFlag) && !US_STATE_CODES.has(form.state.trim().toUpperCase())) {
        e.state = 'Enter a valid 2-letter USPS state code (e.g. CA)';
      }
    }
    if (!form.countryId) e.countryId = 'Country is required';
    const emailError = validateEmail(form.email, { required: true });
    if (emailError) e.email = emailError;
    if (!form.amount.trim()) e.amount = 'Amount is required';
    else if (Number.isNaN(Number(form.amount)) || Number(form.amount) <= 0) e.amount = 'Enter a valid amount';
    if (!form.currency) e.currency = 'Currency is required';
    if (isS2S && !form.isCheckout) {
      const cardDigits = form.cardNumber.replace(/\s/g, '');
      if (!cardDigits) e.cardNumber = 'Card number is required';
      else if (cardDigits.length < 13 || cardDigits.length > 19) e.cardNumber = 'Enter a valid card number';
      if (!form.cardholderName.trim()) e.cardholderName = 'Cardholder name is required';
      if (!form.expiryDate.trim()) e.expiryDate = 'Expiry date is required';
      else if (!/^(0[1-9]|1[0-2])\/\d{2}$/.test(form.expiryDate.trim())) e.expiryDate = 'Use MM/YY format';
      if (!form.cvv.trim()) e.cvv = 'CVV is required';
      else if (!/^\d{3,4}$/.test(form.cvv.trim())) e.cvv = 'Enter a valid CVV';
    }
    if (!form.service.trim()) e.service = 'Service description is required';
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const buildPayload = () => {
    const country = countries.find((c) => String(c.autoId) === form.countryId);
    const terminal = terminalMappings.find((m) => String(m.id) === form.merchantTerminalId);
    const effectiveMerchantId = form.merchantId
      || (authUser?.merchantId ? String(authUser.merchantId) : '')
      || (terminal ? String(terminal.merchantId) : '');
    const merchant = merchants.find((m) => String(m.id) === effectiveMerchantId);

    return {
      // paymentMethodId: Number(form.paymentMethodId),
      // paymentMethod: paymentMethod?.label,
      customerName: form.clientName.trim(),
      address: form.address.trim(),
      zip: formatPostalCodeOnBlur(zipDraftRef.current),
      language: form.language,
      merchantName: merchant?.name ?? (!canSelectMerchant ? authUser?.company || undefined : undefined),
      merchantTerminalId: terminal?.merchantTerminalId,
      callbackUrl: form.callbackUrl.trim(),
      redirectUrl: form.redirectUrl.trim(),
      city: form.city.trim(),
      state: form.state.trim(),
      countryId: Number(form.countryId),
      countryName: country?.countryName,
      customerEmail: form.email.trim(),
      phone: form.phone.trim(),
      amount: Number(form.amount),
      currency: form.currency,
      service: form.service.trim(),
      ...(isS2S && !form.isCheckout
        ? {
            cardNumber: form.cardNumber.replace(/\s/g, ''),
            cardholderName: form.cardholderName.trim(),
            expiryDate: form.expiryDate.trim(),
            cvv: form.cvv.trim(),
          }
        : {}),
    };
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    setApiError(null);
    let submitHeaders: Record<string, string> | undefined;
    let submitPayload: ReturnType<typeof buildPayload> | undefined;
    try {
      submitPayload = buildPayload();
      const flowType = selectedPaymentMethod?.label?.trim().toUpperCase() ?? '';
      const selectedMapping = terminalMappings.find(
        (m) => String(m.id) === form.merchantTerminalId
      );
      const effectiveMerchantId = form.merchantId
        || (authUser?.merchantId ? String(authUser.merchantId) : '')
        || (selectedMapping ? String(selectedMapping.merchantId) : '');
      const credentials = await resolvePaymentCredentials({
        merchantId: effectiveMerchantId ? Number(effectiveMerchantId) : selectedMapping?.merchantId,
        apiKey: selectedMapping?.apiKey,
        merchantSecret: selectedMapping?.merchantSecret,
      });

      if (!credentials) {
        console.error('Create Order Submit Error', {
          payload: submitPayload,
          error:
            'Payment credentials not found for this terminal. Ensure the merchant has an API key and secret configured.',
          selectedTerminal: selectedMapping,
        });
        setApiError(
          'Payment credentials not found for this terminal. Ensure the merchant has an API key and secret configured.'
        );
        return;
      }

      submitHeaders = await buildProcessPaymentHeaders(
        submitPayload,
        flowType,
        credentials,
        form.isCheckout
      );
      const response = await apiService.post(API_PATHS.CREATE_ORDER, submitPayload, {
        headers: submitHeaders,
      });
      const orderResult = extractApiData<{
        orderId?: string;
        checkoutToken?: string;
        checkoutUrl?: string;
        threeDsRequired?: boolean;
        paymentUrl?: string;
      }>(response);

      setSuccessSummary({
        clientName: form.clientName,
        amount: form.amount,
        currency: form.currency,
        email: form.email,
        method: paymentMethods.find((m) => String(m.autoId) === form.paymentMethodId)?.label ?? '—',
        isCheckout: form.isCheckout,
        checkoutUrl: orderResult?.checkoutUrl ? String(orderResult.checkoutUrl) : undefined,
        threeDsRequired: orderResult?.threeDsRequired === true,
        paymentUrl: orderResult?.paymentUrl ? String(orderResult.paymentUrl) : undefined,
      });
      resetForm();
      setDone(true);
    } catch (err: any) {
      console.error('Create Order Submit Error', {
        headers: submitHeaders,
        payload: submitPayload,
        status: err.response?.status,
        response: err.response?.data,
        responseHeaders: err.response?.headers,
      });
      console.error('Create order error:', err);
      setApiError(err.response?.data?.message || 'Failed to create order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    resetForm();
    navigate('/dashboard/transaction-monitoring');
  };

  const handleBackToMonitoring = () => {
    resetForm();
    setSuccessSummary(null);
    setDone(false);
    navigate('/dashboard/transaction-monitoring');
  };

  const handleProceedToPayment = () => {
    const checkoutUrl = successSummary?.checkoutUrl?.trim();
    if (checkoutUrl) {
      const previewUrl = `/embed-preview?checkoutUrl=${encodeURIComponent(checkoutUrl)}`;
      window.open(previewUrl, '_blank', 'noopener,noreferrer');
      return;
    }

    const paymentUrl = successSummary?.threeDsRequired
      ? successSummary?.paymentUrl?.trim()
      : undefined;
    if (!paymentUrl) return;
    window.open(paymentUrl, '_blank', 'noopener,noreferrer');
  };

  if (done && successSummary) {
    return (
      <div className="co-page dh-page">
        <div className="cm-success-wrap">
          <div className="cm-success-icon"><BsCheckCircleFill /></div>
          <h2 className="cm-success-title">Order Created!</h2>
          {successSummary.isCheckout && (
            <div
              style={{
                display: 'inline-block',
                margin: '0 auto 12px',
                padding: '4px 12px',
                borderRadius: '999px',
                background: 'rgba(46, 213, 115, 0.12)',
                border: '1px solid rgba(46, 213, 115, 0.4)',
                color: '#2ed573',
                fontSize: '12px',
                fontWeight: 700,
                letterSpacing: '0.04em',
              }}
            >
              HOSTED CHECKOUT customer pays via checkout link
            </div>
          )}
          <p className="cm-success-sub">
            Payment order for <strong>{successSummary.clientName}</strong> has been created successfully.
            {/* A confirmation has been sent to <strong>{successSummary.email}</strong>. */}
          </p>
          <div className="cm-success-details">
            <div className="cm-sdet"><span>Amount</span><strong>{successSummary.amount} {successSummary.currency}</strong></div>
            <div className="cm-sdet"><span>Email</span><strong>{successSummary.email}</strong></div>
            <div className="cm-sdet"><span>Method</span><strong>{successSummary.method}</strong></div>
          </div>
          <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center' }}>
            <Button type="button" variant="outline" onClick={handleBackToMonitoring}>
              Back to Transactions
            </Button>
            {(successSummary.checkoutUrl || successSummary.threeDsRequired) && (
              <Button
                type="button"
                variant="outline"
                onClick={handleProceedToPayment}
                disabled={
                  !successSummary.checkoutUrl &&
                  !(successSummary.threeDsRequired && successSummary.paymentUrl)
                }
              >
                {successSummary.checkoutUrl ? 'Open Checkout' : 'Proceed to Payment'}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }


  return (
    <div className="co-page dh-page">
      <div className="co-hero">
        <div className="co-hero-deco co-hero-deco-1" />
        <div className="co-hero-deco co-hero-deco-2" />
        {/* <div className="co-hero-deco co-hero-deco-3"><BsCreditCard /></div> */}
        <div className="co-hero-top">
          <div>
            <div className="co-badge"><span className="co-badge-dot" /> New Order</div>
            <h1 className="co-hero-title">Create New Order</h1>
            <p className="co-hero-sub">
              Create and manage customer payment orders quickly and securely.
            </p>
          </div>
          <div className="co-checkout-toggle-row">
            <label className={`co-checkout-toggle ${form.isCheckout ? 'is-on' : ''}`}>
              <span className="co-switch">
                <Input
                  type="checkbox"
                  className="absolute inset-0 h-full min-h-0 w-full cursor-pointer border-0 bg-transparent p-0 opacity-0 shadow-none focus-visible:ring-0"
                  checked={form.isCheckout}
                  onChange={(e) => setForm((prev) => ({ ...prev, isCheckout: e.target.checked }))}
                />
                <span className="co-switch-track" />
              </span>
              Hosted Checkout
            </label>
            <button type="button" className="co-back-btn" onClick={handleCancel}>
              <BsArrowLeft /> Back
            </button>
          </div>
        </div>
        {form.isCheckout && (
          <div className="co-checkout-banner">
            Hosted Checkout is ON card details will be collected on the checkout page. A payment link will be sent to the customer.
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="cm-card co-pay-strip mb-3">
          <div className="co-pay-strip-left">
            <div className="co-pay-icon"><BsCreditCard /></div>
            <div>
              <p className="co-pay-eyebrow">Payment Method</p>
              <p className="co-pay-desc">Select how the customer will pay</p>
            </div>
          </div>
          <div className="co-pay-select-wrap">
            <label className="cm-label">
              <span>Payment Method <em>*</em></span>
              <div className="relative">
                <BsCreditCard className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <SearchableSelect
                  className="h-9 pl-9"
                  value={form.paymentMethodId}
                  options={paymentMethodSelectOptions}
                  onChange={(value) => set('paymentMethodId', value)}
                  placeholder="Select method"
                  searchPlaceholder="Search payment method…"
                  error={!!errors.paymentMethodId}
                  aria-label="Payment method"
                />
              </div>
              {errors.paymentMethodId && <p className="cm-err">{errors.paymentMethodId}</p>}
            </label>
          </div>
        </div>

        <div className="cm-card mb-3">
          <div className="co-section-head">
            <div className="co-section-icon"><BsPersonFill /></div>
            <div>
              <h2 className="co-section-title">Customer Information</h2>
              <p className="co-section-sub">Details about the client placing the order</p>
            </div>
          </div>
          <div className="co-grid-3">
            <label className="cm-label">
              <span>Client Name <em>*</em></span>
              <div className="relative">
                <BsPersonFill className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className={cn('pl-9', errors.clientName && 'border-destructive')}
                  value={form.clientName}
                  onChange={(e) => set('clientName', e.target.value)}
                  placeholder="Full name"
                />
              </div>
              {errors.clientName && <p className="cm-err">{errors.clientName}</p>}
            </label>
            <label className="cm-label">
              <span>Address <em>*</em></span>
              <div className="relative">
                <BsGeoAltFill className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className={cn('pl-9', errors.address && 'border-destructive')}
                  value={form.address}
                  onChange={(e) => set('address', e.target.value)}
                  placeholder="Street address"
                />
              </div>
              {errors.address && <p className="cm-err">{errors.address}</p>}
            </label>
            <label className="cm-label">
              <span>Zip <em>*</em></span>
              <div className="relative">
                <BsHouseFill className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <ZipInput
                  value={form.zip}
                  onChange={handleZipChange}
                  hasError={!!errors.zip}
                  draftRef={zipDraftRef}
                />
              </div>
              {errors.zip && <p className="cm-err">{errors.zip}</p>}
            </label>
            <label className="cm-label">
              <span>Language <em>*</em></span>
              <div className="relative">
                <BsTranslate className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <SearchableSelect
                  className="h-9 pl-9"
                  value={form.language}
                  options={languageSelectOptions}
                  onChange={(value) => set('language', value)}
                  placeholder="Select language"
                  searchPlaceholder="Search language…"
                  error={!!errors.language}
                  aria-label="Language"
                />
              </div>
              {errors.language && <p className="cm-err">{errors.language}</p>}
            </label>
            {canSelectMerchant && (
            <label className="cm-label">
              <span>Merchant <em>*</em></span>
              <div className="relative">
                <BsShop className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <SearchableSelect
                  className="h-9 pl-9"
                  value={form.merchantId}
                  options={merchantSelectOptions}
                  onChange={handleMerchantChange}
                  placeholder="Select merchant"
                  searchPlaceholder="Search merchant…"
                  emptyMessage="No merchants available."
                  error={!!errors.merchantId}
                  aria-label="Merchant"
                />
              </div>
              {errors.merchantId && <p className="cm-err">{errors.merchantId}</p>}
            </label>
            )}
            <label className="cm-label">
              <span>Terminal <em>*</em></span>
              <div className="relative">
                <BsGrid3X3Gap className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <SearchableSelect
                  className="h-9 pl-9"
                  value={form.merchantTerminalId}
                  options={terminalSelectOptions}
                  onChange={handleTerminalChange}
                  placeholder={canSelectMerchant && !form.merchantId ? 'Select merchant first' : 'Select terminal'}
                  searchPlaceholder="Search terminal…"
                  emptyMessage={
                    canSelectMerchant && !form.merchantId
                      ? 'Select a merchant first.'
                      : canSelectMerchant
                        ? 'No terminals for this merchant.'
                        : 'No terminals available.'
                  }
                  disabled={canSelectMerchant && !form.merchantId}
                  error={!!errors.merchantTerminalId}
                  aria-label="Terminal"
                />
              </div>
              {errors.merchantTerminalId && <p className="cm-err">{errors.merchantTerminalId}</p>}
            </label>
           
            <label className="cm-label">
              <span>City <em>*</em></span>
              <div className="relative">
                <BsBuilding className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className={cn('pl-9', errors.city && 'border-destructive')}
                  value={form.city}
                  onChange={(e) => set('city', e.target.value)}
                  placeholder="City"
                />
              </div>
              {errors.city && <p className="cm-err">{errors.city}</p>}
            </label>
            <label className="cm-label">
              <span>State / Province <em>*</em></span>
              <div className="relative">
                <BsFlagFill className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <Input
                  className={cn('pl-9', errors.state && 'border-destructive')}
                  value={form.state}
                  onChange={(e) => set('state', e.target.value)}
                  placeholder="State or province"
                />
              </div>
              {errors.state && <p className="cm-err">{errors.state}</p>}
            </label>
            <label className="cm-label">
              <span>Country <em>*</em></span>
              <div className="relative">
                <BsGlobe2 className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <SearchableSelect
                  className="h-9 pl-9"
                  value={form.countryId}
                  options={countrySelectOptions}
                  onChange={(countryId) => {
                    set('countryId', countryId);
                    const country = countries.find((c) => String(c.autoId) === countryId);
                    if (country?.countryFlag) set('phoneCode', country.countryFlag);
                    if (country?.currency && currencyOptions.includes(country.currency)) {
                      set('currency', country.currency);
                    }
                  }}
                  placeholder="Select country"
                  searchPlaceholder="Search country…"
                  emptyMessage="No countries available."
                  error={!!errors.countryId}
                  aria-label="Country"
                />
              </div>
              {errors.countryId && <p className="cm-err">{errors.countryId}</p>}
            </label>
            <label className="cm-label">
              <span>Mail <em>*</em></span>
              <div className="relative">
                <BsEnvelopeFill className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="email"
                  className={cn('pl-9', errors.email && 'border-destructive')}
                  value={form.email}
                  onChange={(e) => set('email', e.target.value)}
                  placeholder="email@example.com"
                />
              </div>
              {errors.email && <p className="cm-err">{errors.email}</p>}
            </label>
            <label className="cm-label">
              <span>Webhook URL</span>
              <div className="relative">
                <BsLink45Deg className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="url"
                  className={cn('pl-9', errors.callbackUrl && 'border-destructive')}
                  value={form.callbackUrl}
                  onChange={(e) => set('callbackUrl', e.target.value)}
                  placeholder="https://your-domain.com/webhook"
                />
              </div>
              {errors.callbackUrl && <p className="cm-err">{errors.callbackUrl}</p>}
            </label>
            <label className="cm-label">
              <span>Redirect URL</span>
              <div className="relative">
                <BsLink45Deg className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                <Input
                  type="url"
                  className={cn('pl-9', errors.redirectUrl && 'border-destructive')}
                  value={form.redirectUrl}
                  onChange={(e) => set('redirectUrl', e.target.value)}
                  placeholder="https://test.com/redirect"
                />
              </div>
              {errors.redirectUrl && <p className="cm-err">{errors.redirectUrl}</p>}
            </label>
            <label className="cm-label">
              <span>Phone Number <em>*</em></span>
              <div className="co-phone-row">
                <PhoneCodeDropdown
                  value={form.phoneCode}
                  countries={phoneCountries}
                  onChange={(code) => set('phoneCode', code)}
                />
                <div className="relative co-phone-input">
                  <BsTelephoneFill className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    type="tel"
                    inputMode="numeric"
                    className={cn('pl-9', errors.phone && 'border-destructive')}
                    value={form.phone}
                    onChange={(e) => set('phone', e.target.value)}
                    placeholder="Phone number"
                  />
                </div>
              </div>
              {errors.phone && <p className="cm-err">{errors.phone}</p>}
            </label>
          </div>
        </div>

        <div className="cm-card mb-3">
          <div className="co-section-head">
            <div className="co-section-icon"><BsCurrencyDollar /></div>
            <div>
              <h2 className="co-section-title">Payment Information</h2>
              <p className="co-section-sub">Amount, currency, and service description</p>
            </div>
          </div>
          <div className="co-grid-3">
            <label className="cm-label">
              <span>Amount <em>*</em></span>
              <Input
                type="number"
                min="0"
                step="1"
                className={cn('co-amount-input', errors.amount && 'border-destructive')}
                value={form.amount}
                onChange={(e) => set('amount', e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'ArrowUp' || e.key === 'ArrowDown') e.preventDefault();
                }}
                placeholder="Amount"
              />
              {errors.amount && <p className="cm-err">{errors.amount}</p>}
            </label>
            <label className="cm-label">
              <span>Currency <em>*</em></span>
              <div className="relative">
                <span className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-muted-foreground">{getCurrencyIcon(form.currency)}</span>
                <SearchableSelect
                  className="h-9 pl-9"
                  value={form.currency}
                  options={currencySelectOptions}
                  onChange={(value) => set('currency', value)}
                  placeholder={form.merchantTerminalId ? 'Select currency' : 'Select terminal first'}
                  searchPlaceholder="Search currency…"
                  emptyMessage={
                    form.merchantTerminalId
                      ? 'No currencies configured for this terminal.'
                      : 'Select a terminal to load currencies.'
                  }
                  disabled={!form.merchantTerminalId || currencySelectOptions.length === 0}
                  error={!!errors.currency}
                  aria-label="Currency"
                />
              </div>
              {errors.currency && <p className="cm-err">{errors.currency}</p>}
            </label>

            {isS2S && !form.isCheckout && (
              <>
                <label className="cm-label">
                  <span>Card Number <em>*</em></span>
                  <Input
                    type="text"
                    inputMode="numeric"
                    autoComplete="cc-number"
                    className={cn(errors.cardNumber && 'border-destructive')}
                    value={form.cardNumber}
                    onChange={(e) => set('cardNumber', formatCardNumber(e.target.value))}
                    placeholder="4111 1111 1111 1111"
                  />
                  {errors.cardNumber && <p className="cm-err">{errors.cardNumber}</p>}
                </label>
                <label className="cm-label">
                  <span>Cardholder Name <em>*</em></span>
                  <Input
                    type="text"
                    autoComplete="cc-name"
                    className={cn(errors.cardholderName && 'border-destructive')}
                    value={form.cardholderName}
                    onChange={(e) => set('cardholderName', e.target.value)}
                    placeholder="John Doe"
                  />
                  {errors.cardholderName && <p className="cm-err">{errors.cardholderName}</p>}
                </label>
                <label className="cm-label">
                    <span>Expiry Date <em>*</em></span>
                    <Input
                      type="text"
                      inputMode="numeric"
                      autoComplete="cc-exp"
                      className={cn(errors.expiryDate && 'border-destructive')}
                      value={form.expiryDate}
                      onChange={(e) => set('expiryDate', formatExpiryDate(e.target.value))}
                      placeholder="MM/YY"
                    />
                    {errors.expiryDate && <p className="cm-err">{errors.expiryDate}</p>}
                  </label>
                  <label className="cm-label">
                    <span>CVV <em>*</em></span>
                    <Input
                      type="text"
                      inputMode="numeric"
                      autoComplete="cc-csc"
                      className={cn(errors.cvv && 'border-destructive')}
                      value={form.cvv}
                      onChange={(e) => set('cvv', formatCvv(e.target.value))}
                      placeholder="123"
                    />
                    {errors.cvv && <p className="cm-err">{errors.cvv}</p>}
                  </label>
              </>
            )}
            <label className="cm-label co-span3">
              <span>Service <em>*</em></span>
              <div className="co-textarea-wrap">
                <span className="co-char-count">{form.service.length}/{SERVICE_MAX}</span>
                <Textarea
                  className={cn('co-textarea', errors.service && 'border-destructive')}
                  value={form.service}
                  maxLength={SERVICE_MAX}
                  onChange={(e) => set('service', e.target.value)}
                  placeholder="Describe the service or product being purchased..."
                  rows={5}
                />
              </div>
              {errors.service && <p className="cm-err">{errors.service}</p>}
            </label>
          </div>
        </div>

        {apiError && (
          <div className="cm-err" style={{ padding: '0.75rem 1rem', background: 'rgba(255,71,87,0.08)', borderRadius: '8px' }}>
            {apiError}
          </div>
        )}

        <div className="co-footer">
          <div className="co-draft-note">
            <BsCheck2 />
            {lastSaved && lastSaved !== 'restored'
              ? `All changes auto-saved at ${lastSaved}`
              : 'All changes auto-saved as draft'}
          </div>
          <div className="co-footer-actions">
            <Button type="button" variant="outline" onClick={handleCancel}>Cancel</Button>
            <Button type="submit" disabled={loading}>
              <BsPlusCircle /> {loading ? 'Creating…' : 'Create order'}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default CreateOrder;
